import { useState, useEffect } from "react";
import { apiFetch, fmt } from "../api";

// ============================================================================
// LinkedLists (Chunk 7G)
// ----------------------------------------------------------------------------
// Read-only section components used by EditCompanyModal and EditContactModal
// to show what other records are linked to the entity being edited. Rows are
// clickable: clicking a contact row calls onNavigateToContact(id), clicking
// a claim row calls onNavigateToClaim(id). Parent is responsible for closing
// this modal when navigation fires — see App.js for the wiring.
//
// These lists are small by design (LIMIT 200 on the backend). If a company
// somehow ends up with hundreds of contacts we'll grow this into a paged
// viewer, but for now the typical shape is <20.
// ============================================================================

function SectionShell({ title, subtitle, actions, children }) {
  return (
    <div style={{marginTop:16, paddingTop:12, borderTop:"1px solid var(--border)"}}>
      <div style={{fontSize:12, fontWeight:600, color:"var(--navy)", marginBottom:6, display:"flex", alignItems:"center"}}>
        <span style={{flex:1}}>{title}{subtitle && <span style={{fontWeight:400,color:"var(--text3)",marginLeft:6}}>{subtitle}</span>}</span>
        {actions}
      </div>
      {children}
    </div>
  );
}

function LinkedRow({ children, onClick, title }) {
  return (
    <div
      onClick={onClick}
      title={title}
      style={{
        padding:"8px 10px",
        borderBottom:"1px solid var(--border)",
        cursor: onClick ? "pointer" : "default",
        display:"flex",
        alignItems:"center",
        gap:10,
        fontSize:12,
      }}
      onMouseEnter={e => { if (onClick) e.currentTarget.style.background = "#f0f4ff"; }}
      onMouseLeave={e => { if (onClick) e.currentTarget.style.background = "transparent"; }}
    >
      {children}
    </div>
  );
}

// ---- Linked Contacts (for Companies modal) ---------------------------------
// CONTACT-CREATE-1: `actions` (optional node, e.g. "+ Add contact" button)
// renders right-aligned in the section header; `refreshKey` re-fetches the
// list when bumped (after an inline contact create in EditCompanyModal).
export function LinkedContactsList({ companyId, onNavigateToContact, actions, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    if (!companyId) return;
    apiFetch(`/companies/${companyId}/contacts`)
      .then(setRows)
      .catch(e => { try { setErr(JSON.parse(e.message).detail || "Failed to load contacts"); } catch { setErr("Failed to load contacts"); } });
  }, [companyId, refreshKey]);

  if (err)         return <SectionShell title="Linked Contacts" actions={actions}><div className="alert-error">{err}</div></SectionShell>;
  if (rows === null) return <SectionShell title="Linked Contacts" actions={actions}><div style={{fontSize:12,color:"var(--text3)"}}>Loading…</div></SectionShell>;
  if (rows.length === 0) return <SectionShell title="Linked Contacts" actions={actions}><div style={{fontSize:12,color:"var(--text3)"}}>No contacts linked to this company.</div></SectionShell>;

  return (
    <SectionShell title="Linked Contacts" subtitle={`(${rows.length})`} actions={actions}>
      <div style={{border:"1px solid var(--border)", borderRadius:4, background:"#fff"}}>
        {rows.map((c, i) => {
          const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "(unnamed)";
          return (
            <LinkedRow key={c.id} onClick={onNavigateToContact ? () => onNavigateToContact(c.id) : undefined} title={onNavigateToContact ? "Open contact" : undefined}>
              <div style={{flex:"1 1 40%", minWidth:0}}>
                <div style={{fontWeight:600, color:"var(--navy)"}}>{name}</div>
                {c.title && <div style={{fontSize:11,color:"var(--text3)"}}>{c.title}</div>}
              </div>
              <div style={{flex:"1 1 35%", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", color:"var(--text2)"}}>
                {c.email || "—"}
              </div>
              <div style={{flex:"0 0 auto", color:"var(--text2)", fontSize:11}}>
                {c.phone || c.phone_mobile || ""}
              </div>
              {c.needs_admin_review && (
                <span className="badge" style={{background:"#fef3c7",color:"#92400e"}}>Pending</span>
              )}
            </LinkedRow>
          );
        })}
      </div>
    </SectionShell>
  );
}

// ---- Linked Claims (for Company + Contact modals) --------------------------
// The `rolesField` prop names the per-claim roles array returned by the
// backend: "company_roles" for /companies/:id/claims, "contact_roles" for
// /contacts/:id/claims. The shared row shape keeps the rest identical.
export function LinkedClaimsList({ url, rolesField, onNavigateToClaim }) {
  const [rows, setRows] = useState(null);
  const [err,  setErr]  = useState(null);

  useEffect(() => {
    if (!url) return;
    apiFetch(url)
      .then(setRows)
      .catch(e => { try { setErr(JSON.parse(e.message).detail || "Failed to load claims"); } catch { setErr("Failed to load claims"); } });
  }, [url]);

  if (err)         return <SectionShell title="Linked Claims"><div className="alert-error">{err}</div></SectionShell>;
  if (rows === null) return <SectionShell title="Linked Claims"><div style={{fontSize:12,color:"var(--text3)"}}>Loading…</div></SectionShell>;
  if (rows.length === 0) return <SectionShell title="Linked Claims"><div style={{fontSize:12,color:"var(--text3)"}}>No claims linked.</div></SectionShell>;

  return (
    <SectionShell title="Linked Claims" subtitle={`(${rows.length})`}>
      <div style={{border:"1px solid var(--border)", borderRadius:4, background:"#fff"}}>
        {rows.map(c => {
          const roles = Array.isArray(c[rolesField]) ? c[rolesField] : [];
          return (
            <LinkedRow key={c.id} onClick={onNavigateToClaim ? () => onNavigateToClaim(c.id) : undefined} title={onNavigateToClaim ? "Open claim" : undefined}>
              <div style={{flex:"0 0 120px"}}>
                <span className="mono" style={{color:"var(--blue)", fontWeight:600, fontSize:12}}>{c.claim_number}</span>
              </div>
              <div style={{flex:"1 1 auto", minWidth:0, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                <span style={{color:"var(--text1)"}}>{c.claimant_name || "—"}</span>
                <span style={{color:"var(--text3)", marginLeft:6}}>
                  {c.insurer ? `· ${c.insurer}` : ""}
                </span>
              </div>
              <div style={{flex:"0 0 auto", fontSize:11, color:"var(--text3)"}}>
                {fmt.date(c.date_of_loss) || "—"}
              </div>
              <div style={{flex:"0 0 auto"}}>
                <span className="badge" style={{background:"#e0e7ff", color:"#3730a3"}}>{c.claim_status || "—"}</span>
              </div>
              {roles.length > 0 && (
                <div style={{flex:"0 0 auto", fontSize:11, color:"var(--text2)"}}>
                  as {roles.join(", ")}
                </div>
              )}
            </LinkedRow>
          );
        })}
      </div>
    </SectionShell>
  );
}
